import { OperationalStatus, Store } from '@components/stores-locator/model';
import { ElasticSearchResponse } from '@services/elastic-search/models/elastic-search-response';
import { StoreDocument } from '@services/elastic-search/models/store-document';
import { LogsService } from '@services/logs';
import { WeeklyOpeningHours } from '@shared/models';
import { WeekDay } from '@shared/types';

const parseOperationalStatus = (raw: string | undefined): OperationalStatus | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as OperationalStatus;
    if (!parsed?.code) return null;
    return parsed;
  } catch (error) {
    LogsService.error('storesSerializer', 'Error during JSON conversion of operational_status', error, raw);
    return null;
  }
};

const parseSchedules = (schedulesJson: string | undefined): { slots: string }[] => {
  if (!schedulesJson) return [];
  try {
    return JSON.parse(schedulesJson) as { slots: string }[];
  } catch (error) {
    LogsService.error('storesSerializer', 'Error during JSON conversion of schedules', error, schedulesJson);
    return [];
  }
};

const parseWeeklyOpeningHours = (raw: string | undefined): WeeklyOpeningHours | undefined => {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as WeeklyOpeningHours;
  } catch (error) {
    LogsService.error('storesSerializer', 'Error during JSON conversion of weekly_opening_hours', error, raw);
    return undefined;
  }
};

const parseClosedDays = (schedules: { slots: string }[]): WeekDay[] =>
  schedules.reduce<WeekDay[]>((closed, schedule, index) => {
    if (schedule.slots.toLowerCase() === 'fermé') closed.push(index as WeekDay);
    return closed;
  }, []);

export const storesSerializer = (stores: ElasticSearchResponse<StoreDocument>, includeDistance = false): Store[] =>
  stores.hits.hits.map((hit) => {
    let distance = null;
    if (includeDistance) {
      // Rounded, not truncated: a store 900 m away must not read "0 km".
      distance = hit.sort[0] ? Math.round(hit.sort[0]) : null;
    }
    const source = hit._source;
    const schedules = parseSchedules(source.schedules?.[0]);
    return {
      uuid: source.uuid[0],
      name: source.title[0],
      address: source.field_adresse[0],
      coords: {
        lat: source.field_latitude[0],
        lng: source.field_longitude[0],
      },
      distance: distance,
      opening: null!,
      url: source.url[0],
      phone: source.field_telephone?.[0] ?? '',
      isHomeAppointment: !source.home_rdv_disabled[0],
      homeAppointmentDays: source.home_rdv_day,
      schedules,
      closedDays: parseClosedDays(schedules),
      operationalStatus: parseOperationalStatus(source.operational_status?.[0]),
      weeklyOpeningHours: parseWeeklyOpeningHours(source.weekly_opening_hours?.[0]),
    };
  });
